/**
 * BXGY Motivation Bar — Vue 3 controller.
 *
 * Liquid (snippets/bxgy-motivation-bar.liquid) supplies the
 * HTML template; this file owns the reactive state and keeps the
 * tier list in sync with the current cart.
 *
 * Data flow:
 *   1. On mount, read rules from the `<script data-bxgy-rules>`
 *      blob emitted by sections/bxgy-auto-add-rules.liquid.
 *   2. Fetch /cart.js once for an initial cart snapshot.
 *   3. Listen for `bxgy:cart-updated` events from
 *      assets/bxgy-auto-add.js -- the reconciler dispatches these
 *      after every cart mutation it performs.
 *   4. Everything else is `computed()` off `cart` + `config`.
 *
 * Multiple mounts are supported: the cart page and the cart
 * drawer both render the snippet, so we init one Vue app per
 * mount and re-init fresh mounts that appear after drawer
 * re-renders (via MutationObserver).
 *
 * Depends on vue.global.prod.js being loaded first (window.Vue).
 */
(function (global) {
  'use strict';

  const DEFAULT_TITLE = 'Unlock a free gift with your order';

  /**
   * Read and merge all `<script data-bxgy-rules>` tags. Mirrors
   * the logic in bxgy-auto-add.js: dedupes by rule id so double
   * rendering can't cause phantom tiers.
   */
  const readRuleConfig = () => {
    const nodes = document.querySelectorAll('script[data-bxgy-rules]');
    const combined = { enabled_global: false, rules: [] };
    const seenIds = new Set();
    for (const node of nodes) {
      try {
        const parsed = JSON.parse(node.textContent || '{}');
        if (parsed.enabled_global) combined.enabled_global = true;
        if (Array.isArray(parsed.rules)) {
          for (const rule of parsed.rules) {
            if (!rule || !rule.id) continue;
            if (seenIds.has(rule.id)) continue;
            seenIds.add(rule.id);
            combined.rules.push(rule);
          }
        }
      } catch (e) {
        // Ignore parse errors here -- the reconciler logs them
        // from its own copy of this function.
      }
    }
    return combined;
  };

  /**
   * Pick the right threshold (in cents) for the given cart
   * currency. CAD is the primary / fallback. USD has its own
   * value when set; otherwise falls back to CAD.
   */
  const getThresholdCents = (rule, currencyCode) => {
    const currency = String(currencyCode || '').toUpperCase();
    const cadCents = Number(rule.min_spend_cad_cents) || 0;
    const usdCents = Number(rule.min_spend_usd_cents) || 0;
    if (currency === 'USD' && usdCents > 0) return usdCents;
    return cadCents;
  };

  const isAutoAddedLine = (item) => {
    if (!item || !item.properties) return false;
    return String(item.properties._bxgy_auto) === 'true';
  };

  /**
   * Sum of qualifying-line prices (in cents), excluding any
   * auto-added line items so gift lines don't bootstrap more
   * gifts.
   */
  const getEligibleSubtotal = (cart, rule) => {
    if (!cart || !Array.isArray(cart.items)) return 0;
    const scope = rule && rule.eligible_scope === 'entire_cart'
      ? 'entire_cart'
      : 'collection_filter';
    const eligibleIds = new Set((rule.eligible_product_ids || []).map(String));
    let subtotal = 0;
    for (const item of cart.items) {
      if (isAutoAddedLine(item)) continue;
      if (scope !== 'entire_cart' && !eligibleIds.has(String(item.product_id))) continue;
      const linePrice = typeof item.final_line_price === 'number'
        ? item.final_line_price
        : item.line_price;
      subtotal += linePrice || 0;
    }
    return subtotal;
  };

  /**
   * Returns the amount as a rounded whole-dollar string prefixed
   * with "$". `currencyCode` is kept in the signature for
   * backwards compatibility but is intentionally ignored.
   */
  const formatMoneyCents = (cents, currencyCode) => {
    const amount = Math.max(0, cents || 0) / 100;
    return `$${Math.round(amount)}`;
  };

  const fetchCart = () => {
    let root = '/';
    try {
      if (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) {
        root = global.Shopify.routes.root;
      }
    } catch (e) { /* noop */ }
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return fetch(`${root}cart.js`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  };

  // Track every mounted app so MutationObserver can tear down
  // instances whose mount element has been removed (cart drawer
  // re-renders replace the mount node wholesale). Keyed by the
  // mount element itself via a Map for O(1) lookup.
  const mountedApps = new Map();

  const mountBxgyMotivationBar = (mountEl) => {
    if (!mountEl) return;
    if (mountedApps.has(mountEl)) return;
    if (!global.Vue || typeof global.Vue.createApp !== 'function') {
      console.warn('[BXGY motivation] Vue not found; skipping mount.');
      return;
    }

    const config = readRuleConfig();
    if (!config.enabled_global || !Array.isArray(config.rules) || config.rules.length === 0) {
      // Nothing to show; leave the mount hidden (snippet renders
      // with `hidden` attr by default so there's no flash).
      return;
    }

    const { createApp, ref, computed, onMounted, onBeforeUnmount, watch } = global.Vue;

    const titleAttr = mountEl.getAttribute('data-bxgy-title');
    const title = titleAttr && titleAttr.length > 0 ? titleAttr : DEFAULT_TITLE;

    const app = createApp({
      name: 'BxgyMotivationBar',
      // Match the project-wide convention (rewards-program.liquid
      // uses the same delimiters).
      delimiters: ['[[', ']]'],
      setup() {
        const cart = ref(null);

        const currency = computed(() => (cart.value && cart.value.currency) || 'CAD');

        const tiers = computed(() => {
          const cur = currency.value;
          const ruleList = (config.rules || []).filter((r) => {
            if (!r || !r.enabled) return false;
            if (!r.reward_variant_id) return false;
            return getThresholdCents(r, cur) > 0;
          });
          ruleList.sort((a, b) => getThresholdCents(a, cur) - getThresholdCents(b, cur));

          return ruleList.map((r) => {
            const threshold = getThresholdCents(r, cur);
            const subtotal = cart.value ? getEligibleSubtotal(cart.value, r) : 0;
            const pct = threshold > 0
              ? Math.min(100, (subtotal / threshold) * 100)
              : 0;
            const complete = subtotal >= threshold && threshold > 0;
            const remaining = Math.max(0, threshold - subtotal);
            return {
              id: String(r.id),
              threshold,
              pct: Number(pct.toFixed(1)),
              complete,
              rewardName: r.reward_product_title || 'free gift',
              amountText: `add ${formatMoneyCents(remaining, cur)} for a free`,
            };
          });
        });

        // Toggle the mount element's `hidden` attr based on whether
        // there's anything to show. We manipulate the mount root
        // directly because Vue's root bindings don't propagate to
        // the element you call .mount(el) on -- only its children.
        watch(tiers, (val) => {
          if (!val || val.length === 0) {
            mountEl.setAttribute('hidden', '');
          } else {
            mountEl.removeAttribute('hidden');
          }
        }, { immediate: true });

        const handleCartUpdated = (e) => {
          if (e && e.detail && e.detail.cart) {
            cart.value = e.detail.cart;
          } else {
            // Fallback: someone fired the event without a payload.
            fetchCart().then((c) => { if (c) cart.value = c; });
          }
        };

        onMounted(() => {
          document.addEventListener('bxgy:cart-updated', handleCartUpdated);
          // Initial snapshot in case we mount after the last
          // cart-update event (e.g. drawer opened mid-session).
          fetchCart().then((c) => { if (c) cart.value = c; });
        });

        onBeforeUnmount(() => {
          document.removeEventListener('bxgy:cart-updated', handleCartUpdated);
        });

        return { title, tiers };
      },
    });

    try {
      app.mount(mountEl);
      mountedApps.set(mountEl, app);
    } catch (e) {
      console.warn('[BXGY motivation] mount failed', e);
    }
  };

  /**
   * Scan the document for any `[data-bxgy-motivation-mount]`
   * elements that aren't mounted yet, and tear down apps whose
   * mount node has been detached from the DOM. Safe to call any
   * number of times.
   */
  const initBxgyMotivationBars = () => {
    const mounts = document.querySelectorAll('[data-bxgy-motivation-mount]');
    mounts.forEach(mountBxgyMotivationBar);

    // Garbage-collect orphaned apps (their mount node was yanked
    // from the DOM, e.g. cart drawer replaced its innerHTML).
    mountedApps.forEach((app, el) => {
      if (!document.body.contains(el)) {
        try { app.unmount(); } catch (e) { /* noop */ }
        mountedApps.delete(el);
      }
    });
  };

  global.mountBxgyMotivationBar = mountBxgyMotivationBar;
  global.initBxgyMotivationBars = initBxgyMotivationBars;

  const bootstrap = () => {
    initBxgyMotivationBars();
    try {
      // A single init call handles both: new mounts get mounted,
      // dead mounts get unmounted. Cheap enough to debounce-free.
      const obs = new MutationObserver(() => initBxgyMotivationBars());
      obs.observe(document.body, { childList: true, subtree: true });
    } catch (e) { /* MutationObserver ubiquitous; noop on failure */ }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})(typeof window !== 'undefined' ? window : this);
